package com.nonopn.skydome.common.app.pages.estimates

import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.pages.probes.isProbeDeleted
import com.nonopn.skydome.common.app.pages.probes.normalizeForSearch

// Pure logic of the admin estimates list: status of an estimate, search and status filter.
// Kept free of any UI / model dependency so that it can be unit tested in isolation.

/**
 * Status of an estimate. The admin list shows the state computed by the server (the 11 values of
 * `EstimateState`); the legacy values are also used by the user list, derived from areEnabled / end date.
 */
enum class EstimateStatus {
    ACTIVE, PENDING_PAYMENT, EXPIRED, EXTERNAL_ACTIVE, EXTERNAL_DISABLED,
    EMPTY, DEMO, DEMO_EXPIRED, EXTERNAL_PARTIAL, EXTERNAL_EXPIRED, PAYMENT_ISSUE, ENDED
}

/** Status chips of the list: [ALL] keeps every estimate; the other chips split the statuses without overlap. */
enum class EstimateStatusFilter { ALL, ACTIVE, PENDING_PAYMENT, EXPIRED, EXTERNAL_CONTRACT, DEMO, PAYMENT_ISSUE, EMPTY }

/**
 * An externally managed contract always wins over the payment states. Rule (Kevin): it is active only
 * when areEnabled is true, otherwise disabled (never "pending payment"); the end date is not used.
 * Legacy rule, used when the server does not give a state.
 */
fun resolveEstimateStatus(
    areEnabled: Boolean,
    validUntilSecondsUtc: Long?,
    nowSeconds: Long,
    isExternalContract: Boolean = false,
): EstimateStatus = when {
    isExternalContract && areEnabled -> EstimateStatus.EXTERNAL_ACTIVE
    isExternalContract -> EstimateStatus.EXTERNAL_DISABLED
    areEnabled && (validUntilSecondsUtc == null || validUntilSecondsUtc > nowSeconds) -> EstimateStatus.ACTIVE
    !areEnabled && validUntilSecondsUtc == null -> EstimateStatus.PENDING_PAYMENT
    else -> EstimateStatus.EXPIRED
}

/** Status of a server `EstimateState` value, or null for an unknown value. */
fun estimateStatusFromState(state: String?): EstimateStatus? = when (state?.trim()) {
    "Empty" -> EstimateStatus.EMPTY
    "Demo" -> EstimateStatus.DEMO
    "DemoExpired" -> EstimateStatus.DEMO_EXPIRED
    "ExternalActive" -> EstimateStatus.EXTERNAL_ACTIVE
    "ExternalPartial" -> EstimateStatus.EXTERNAL_PARTIAL
    "ExternalDisabled" -> EstimateStatus.EXTERNAL_DISABLED
    "ExternalExpired" -> EstimateStatus.EXTERNAL_EXPIRED
    "PendingPayment" -> EstimateStatus.PENDING_PAYMENT
    "Active" -> EstimateStatus.ACTIVE
    "PaymentIssue" -> EstimateStatus.PAYMENT_ISSUE
    "Ended" -> EstimateStatus.ENDED
    else -> null
}

/** The state given by the server when there is one (admin list), the legacy rule otherwise. */
fun ThunderDetectionEstimate.resolveStatus(nowSeconds: Long): EstimateStatus =
    estimateStatusFromState(admin?.state)
        ?: resolveEstimateStatus(areEnabled, validUntilSecondsUtc, nowSeconds, isExternalContract)

private fun EstimateStatusFilter.accepts(status: EstimateStatus) = when (this) {
    EstimateStatusFilter.ALL -> true
    EstimateStatusFilter.ACTIVE -> status == EstimateStatus.ACTIVE
    EstimateStatusFilter.PENDING_PAYMENT -> status == EstimateStatus.PENDING_PAYMENT
    EstimateStatusFilter.EXPIRED -> status == EstimateStatus.EXPIRED ||
        status == EstimateStatus.DEMO_EXPIRED || status == EstimateStatus.ENDED
    EstimateStatusFilter.EXTERNAL_CONTRACT -> status == EstimateStatus.EXTERNAL_ACTIVE ||
        status == EstimateStatus.EXTERNAL_PARTIAL || status == EstimateStatus.EXTERNAL_DISABLED ||
        status == EstimateStatus.EXTERNAL_EXPIRED
    EstimateStatusFilter.DEMO -> status == EstimateStatus.DEMO
    EstimateStatusFilter.PAYMENT_ISSUE -> status == EstimateStatus.PAYMENT_ISSUE
    EstimateStatusFilter.EMPTY -> status == EstimateStatus.EMPTY
}

private fun ThunderDetectionEstimate.matchesSearch(needle: String) = needle.isEmpty() ||
    normalizeForSearch(email).contains(needle) ||
    normalizeForSearch(uuid).contains(needle) ||
    probes.any { normalizeForSearch(it.name).contains(needle) }

/**
 * Estimates kept by [query] (case and accent insensitive, searched in the account e-mail, the estimate uuid
 * and the probe names) and by [statusFilter]. Estimates without probe are kept (state Empty). The order of
 * [list] is kept.
 */
fun filterEstimates(
    list: List<ThunderDetectionEstimate>,
    query: String,
    statusFilter: EstimateStatusFilter,
    nowSeconds: Long,
): List<ThunderDetectionEstimate> {
    val needle = normalizeForSearch(query)
    return list.filter { statusFilter.accepts(it.resolveStatus(nowSeconds)) && it.matchesSearch(needle) }
}

private val technicalIdRegex = Regex("^[0-9a-fA-F]{8}(-?[0-9a-fA-F]{4}){3}-?[0-9a-fA-F]{12}([_-].*)?$")

/** True when [name] looks like a technical identifier (a uuid, or "uuid_uuid") rather than a label. */
fun looksLikeTechnicalId(name: String): Boolean {
    val trimmed = name.trim()
    return trimmed.isEmpty() || technicalIdRegex.matches(trimmed)
}

/** First 8 characters of an identifier, used as a small readable reference. */
fun shortId(id: String): String = id.trim().take(8)

/** Readable name of a probe: its own name unless it is a technical id, then null (use "Probe N"). */
fun readableProbeName(name: String): String? = name.trim().takeUnless { looksLikeTechnicalId(it) }

/** True when the estimate has at least one probe that is not deleted. */
fun ThunderDetectionEstimate.hasVisibleProbes(): Boolean = probes.any { !isProbeDeleted(it.status) }

/** Number of probes of the estimate that are not deleted. */
fun ThunderDetectionEstimate.visibleProbeCount(): Int = probes.count { !isProbeDeleted(it.status) }

/**
 * Creation date of an estimate (unix seconds): the one of the server when given, else the oldest known probe
 * creation, or null when unknown.
 */
fun ThunderDetectionEstimate.createdAtSecondsUtc(): Long? =
    admin?.createdAtSecondsUtc?.takeIf { it > 0L }
        ?: probes.map { it.createdAtSecondsUtc }.filter { it > 0L }.minOrNull()

/** Localizable key of an anomaly (`EstimateIssueCode`), or null for a code this app does not know. */
enum class EstimateIssueKind { PROBE_OWNER, PROBE_APP, PROBE_DEMO, PROBE_END, PROBE_EXPIRED,
    UNPAID_ACTIVE_PROBES, SUBSCRIPTION_QUANTITY, EXTERNAL_SUBSCRIPTION }

fun estimateIssueKind(code: String?): EstimateIssueKind? = when (code?.trim()) {
    "ProbeOwner" -> EstimateIssueKind.PROBE_OWNER
    "ProbeApp" -> EstimateIssueKind.PROBE_APP
    "ProbeDemo" -> EstimateIssueKind.PROBE_DEMO
    "ProbeEnd" -> EstimateIssueKind.PROBE_END
    "ProbeExpired" -> EstimateIssueKind.PROBE_EXPIRED
    "UnpaidActiveProbes" -> EstimateIssueKind.UNPAID_ACTIVE_PROBES
    "SubscriptionQuantity" -> EstimateIssueKind.SUBSCRIPTION_QUANTITY
    "ExternalSubscription" -> EstimateIssueKind.EXTERNAL_SUBSCRIPTION
    else -> null
}
